'use client';

import { useAtomValue } from 'jotai/index';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  useEffect,
  useState,
} from 'react';

import { currentCurrencyAtom } from '@/features/currency/atoms/currencies-atom';
import getBudgetFilterValues from '@/features/currency/requests/get-budget-filter-values';
import ListingFilterNew from '@/features/listing/components/listings-filters/listing-filter-new/listing-filter-new';
import { LISTINGS_BUDGET_FILTER } from '@/features/listing/constants/listings-filters-constants';
import {
  type ListingsFilterName,
  type ListingsRangeFilter,
  PaginationNames,
} from '@/features/listing/types/listing-filters-types';
import { useRouter } from '@/i18n/routing';

import ListingFilterRange
  from '../listing-filter-range/listing-filter-range';

interface ListingBudgetFilterProps {
  onFilterDisplayChange: (filterName: ListingsFilterName | null) => void;
  setFilterLink: (link: { pathname: string; searchParams: URLSearchParams }) => void;
  filterLink: {
    pathname: string;
    searchParams: URLSearchParams;
  },
}

export default function ListingBudgetFilter(props: ListingBudgetFilterProps) {
  const {
    filterLink,
    onFilterDisplayChange,
    setFilterLink,
  } = props;

  const currency = useAtomValue(currentCurrencyAtom);
  const searchParams = useSearchParams();
  const router = useRouter();

  const budgetMinValue = Number(searchParams.get(LISTINGS_BUDGET_FILTER.options.min.name));
  const budgetMaxValue = Number(searchParams.get(LISTINGS_BUDGET_FILTER.options.max.name));

  const [budgetFilter,
    setBudgetFilter] = useState<ListingsRangeFilter>(LISTINGS_BUDGET_FILTER);
  const [isPopoverOpen,
    setPopoverOpen] = useState(false);

  const translationsFilterBudget = useTranslations('listing.filters.budget');

  const budgetButtonTitleMin = budgetMinValue || budgetFilter.options.min.defaultValue;
  const budgetButtonTitleMax = budgetMaxValue || budgetFilter.options.max.defaultValue;

  const budgetButtonTitle = budgetMinValue || budgetMaxValue
    ? `${String(budgetButtonTitleMin)} - ${String(budgetButtonTitleMax)}`
    : translationsFilterBudget('button');

  const handleClear = () => {
    filterLink.searchParams.delete(budgetFilter.options.min.name);
    filterLink.searchParams.delete(budgetFilter.options.max.name);

    budgetFilter.options.max.value = budgetFilter.options.max.defaultValue;
    budgetFilter.options.min.value = budgetFilter.options.min.defaultValue;

    setBudgetFilter(budgetFilter);

    setFilterLink({
      pathname: filterLink.pathname,
      searchParams: filterLink.searchParams,
    });
  };

  const handleChangeFilterLink = (updatedFilterLink: ListingBudgetFilterProps['filterLink']) => {
    if (updatedFilterLink.searchParams.has(PaginationNames.PageNumber)) {
      updatedFilterLink.searchParams.delete(PaginationNames.PageNumber);
    }

    setFilterLink(updatedFilterLink);
  };

  const handleApplyClick = () => {
    router.push(`${filterLink.pathname}?${filterLink.searchParams.toString()}`);
  };

  useEffect(() => {
    const cloneFilter = JSON.stringify(LISTINGS_BUDGET_FILTER);
    const updatedFilter = JSON.parse(cloneFilter) as ListingsRangeFilter;

    // TODO: error handling
    getBudgetFilterValues(currency.name)
      .then((values) => {
        updatedFilter.options.max.defaultValue = values.max;
        updatedFilter.options.max.value = budgetButtonTitleMax;
        updatedFilter.options.min.defaultValue = values.min;
        updatedFilter.options.min.value = budgetButtonTitleMin;
        setBudgetFilter(updatedFilter);
      })
      .catch(() => null);
  }, [currency, budgetButtonTitleMax, budgetButtonTitleMin]);

  return (
    <ListingFilterNew
      buttonText={budgetButtonTitle}
      dialogTitle={translationsFilterBudget('dropdown-title')}
      onFilterDisplayChange={onFilterDisplayChange}
      listingFilterName="listingsBudget"
      isPopoverOpen={isPopoverOpen}
      setPopoverOpen={setPopoverOpen}
      onClear={handleClear}
      onApplyClick={handleApplyClick}
    >
      <ListingFilterRange
        filterLink={filterLink}
        filter={budgetFilter}
        setFilterLink={handleChangeFilterLink}
      />
    </ListingFilterNew>
  );
}
